import {
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
  StarFilled,
  UploadOutlined,
} from '@ant-design/icons'
import { Modal, Select } from 'antd'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import Image, { useProductImages } from '@/components/Atoms/Image/Image'
import Loader from '@/components/Atoms/Loader/Loader'
import { getFileIcon } from '@/utils/files/getFileIcon'

import type { AttachmentType } from '../../../../AttributeProcessing.types'
import { useAttributeProcessingContext } from '../../../../AttributeProcessingContext'
import cn from './AttachmentsTab.module.scss'

function getExtsFromAccept(accept: string): string[] {
  return accept.split(',').map((e) => e.trim().replace('.', '').toLowerCase())
}

type SubTab = 'IMAGES' | 'DOCUMENTS'

const IMAGE_ACCEPT = '.docx,.jfif,.jpeg,.jpg,.pdf,.png,.webp'

const DOC_CATEGORIES: {
  type: Exclude<AttachmentType, 'IMAGE'>
  label: string
  accept: string
}[] = [
  {
    type: 'PASSPORT',
    label: 'Паспорт',
    accept: '.doc,.docx,.jpeg,.jpg,.pdf,.png,.xlsx',
  },
  { type: 'SCHEME', label: 'Схема', accept: '.jpg,.pdf,.png' },
  {
    type: 'PROMO_MATERIAL',
    label: 'Рекламный материал',
    accept: '.jpg,.pdf,.pptx',
  },
  { type: 'CERTIFICATE', label: 'Сертификат', accept: '.jpg,.pdf,.png' },
]

const IMAGE_TYPE_OPTIONS: { value: AttachmentType; label: string }[] = [
  { value: 'IMAGE', label: 'Фото' },
  { value: 'PASSPORT', label: 'Паспорт' },
]

function AttachmentsTab() {
  const {
    activeTab,
    selectedIds,
    selectedDetails,
    dragOver,
    setDragOver,
    uploading,
    attachDocsLoading,
    attachDocs,
    downloadAttachFile,
    deleteAttachFile,
    updateAttachFileType,
    isRightPanelDisabled,
    pendingFiles,
    mainPendingImageId,
    setMainPendingImageId,
    addPendingFiles,
    removePendingFile,
    uploadPendingFiles,
    imageVersion,
  } = useAttributeProcessingContext()

  const [modal, modalContextHolder] = Modal.useModal()

  const isSingle = selectedIds.size === 1 && selectedDetails.length === 1
  const singleCode = isSingle ? selectedDetails[0].code : null
  const isMulti = selectedIds.size > 1

  const attachmentsImageCode = activeTab === 'ATTACHMENTS' ? singleCode : null
  const { images: productImages, loading: productImagesLoading } =
    useProductImages(attachmentsImageCode, imageVersion, 'all')

  const [subTab, setSubTab] = useState<SubTab>('IMAGES')
  const [dragOverCat, setDragOverCat] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const confirmDelete = (
    fileName: string,
    fileType: AttachmentType,
    displayName: string
  ) => {
    modal.confirm({
      title: 'Удалить файл?',
      content: `«${displayName}» будет удалён из карточки товара.`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      centered: true,
      maskClosable: true,
      onOk: () => deleteAttachFile(fileName, fileType),
    })
  }

  useEffect(() => {
    const onEnter = () => setIsDragging(true)
    const onLeave = (e: DragEvent) => {
      if (!e.relatedTarget) setIsDragging(false)
    }
    const onDone = () => setIsDragging(false)
    document.addEventListener('dragenter', onEnter)
    document.addEventListener('dragleave', onLeave)
    document.addEventListener('drop', onDone)
    document.addEventListener('dragend', onDone)
    return () => {
      document.removeEventListener('dragenter', onEnter)
      document.removeEventListener('dragleave', onLeave)
      document.removeEventListener('drop', onDone)
      document.removeEventListener('dragend', onDone)
    }
  }, [])

  const handleDocFiles = (
    files: FileList | File[] | null,
    cat: (typeof DOC_CATEGORIES)[0]
  ) => {
    if (!files) return
    const allFiles = Array.from(files)
    const allowed = getExtsFromAccept(cat.accept)
    const valid = allFiles.filter((f) =>
      allowed.includes(f.name.split('.').pop()?.toLowerCase() ?? '')
    )
    const invalid = allFiles.filter(
      (f) => !allowed.includes(f.name.split('.').pop()?.toLowerCase() ?? '')
    )
    if (invalid.length > 0) {
      toast.warning(
        `Недопустимый формат для «${cat.label}»: ${invalid.map((f) => f.name).join(', ')}`
      )
    }
    if (valid.length > 0) {
      addPendingFiles(valid, cat.type)
    }
  }

  const handleDocDrop = (
    e: React.DragEvent,
    cat: (typeof DOC_CATEGORIES)[0]
  ) => {
    e.preventDefault()
    setDragOverCat(null)
    if (uploading) return
    handleDocFiles(e.dataTransfer.files, cat)
  }

  if (isRightPanelDisabled) {
    return (
      <div className={cn.infoSection}>
        <div className={cn.rightPanelPlaceholder}>
          <span>Выберите номенклатуры в левой таблице</span>
        </div>
      </div>
    )
  }

  const pendingImages = pendingFiles.filter((f) => f.type === 'IMAGE')
  const pendingDocs = pendingFiles.filter((f) => f.type !== 'IMAGE')
  const currentScopePending = subTab === 'IMAGES' ? pendingImages : pendingDocs
  const MAX_MB = 25
  const totalMB =
    currentScopePending.reduce((s, f) => s + f.file.size, 0) / (1024 * 1024)
  const isOverLimit = totalMB > MAX_MB

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (uploading) return
    addPendingFiles(e.dataTransfer.files, 'IMAGE')
  }

  return (
    <div className={cn.infoSection}>
      {modalContextHolder}

      {/* ── Sub-tab toggle ── */}
      <div className={cn.subTabBar}>
        <button
          className={[
            cn.subTabBtn,
            subTab === 'IMAGES' ? cn.subTabBtnActive : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setSubTab('IMAGES')}
        >
          Изображения
          {pendingImages.length > 0 && (
            <span className={cn.subTabBadge}>{pendingImages.length}</span>
          )}
        </button>
        <button
          className={[
            cn.subTabBtn,
            subTab === 'DOCUMENTS' ? cn.subTabBtnActive : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setSubTab('DOCUMENTS')}
        >
          Документы
          {pendingDocs.length > 0 && (
            <span className={cn.subTabBadge}>{pendingDocs.length}</span>
          )}
        </button>
      </div>

      {isMulti && (
        <div className={cn.attachMultiBanner}>
          Файлы будут загружены для {selectedIds.size} номенклатур
        </div>
      )}

      {/* ── Upload bar ── */}
      {currentScopePending.length > 0 && (
        <div className={cn.globalUploadBar}>
          <div className={cn.pendingTitleGroup}>
            <span className={cn.pendingTitle}>
              Ожидают загрузки: {currentScopePending.length}
              {' · '}
              <span className={isOverLimit ? cn.pendingSizeOver : ''}>
                {totalMB.toFixed(1)} МБ
              </span>
              {' / '}
              {MAX_MB} МБ
            </span>
            {isOverLimit && (
              <span className={cn.pendingLimitWarning}>
                Превышен лимит — уберите часть файлов
              </span>
            )}
          </div>
          <button
            className={[
              cn.uploadBtn,
              uploading || isOverLimit ? cn.uploadBtnDisabled : '',
            ]
              .filter(Boolean)
              .join(' ')}
            disabled={uploading || isOverLimit}
            onClick={() => uploadPendingFiles(subTab)}
          >
            <CloudUploadOutlined />
            {uploading ? 'Загрузка...' : 'Загрузить'}
          </button>
        </div>
      )}

      <div className={cn.attachPanel}>
        {/* ══════════════ IMAGES TAB ══════════════ */}
        {subTab === 'IMAGES' && (
          <>
            {/* Drop zone */}
            <div
              className={[
                cn.attachDropZone,
                dragOver ? cn.attachDropZoneActive : '',
                uploading ? cn.attachDropZoneDisabled : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onDragOver={(e) => {
                e.preventDefault()
                if (!uploading) setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              <label
                className={[
                  cn.attachUploadBtn,
                  uploading ? cn.attachUploadBtnDisabled : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <UploadOutlined />
                Выбрать файлы
                <input
                  type="file"
                  multiple
                  accept={IMAGE_ACCEPT}
                  className={cn.hiddenFileInput}
                  disabled={uploading}
                  onChange={(e) => {
                    addPendingFiles(e.target.files, 'IMAGE')
                    ;(e.target as HTMLInputElement).value = ''
                  }}
                />
              </label>
              <div className={cn.attachDropHints}>
                <span className={cn.attachDropHint}>
                  или перетащите файлы в эту область
                </span>
                <span className={cn.attachSizeLimit}>до 25 МБ суммарно</span>
              </div>
            </div>

            {/* Pending images grid */}
            {pendingImages.length > 0 && (
              <div className={cn.pendingSection}>
                <div className={cn.pendingImageGrid}>
                  {pendingImages.map((pf) => {
                    const isMain = pf.id === mainPendingImageId
                    return (
                      <div
                        key={pf.id}
                        className={[
                          cn.pendingImageItem,
                          isMain ? cn.pendingImageItemMain : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => setMainPendingImageId(pf.id)}
                        title={isMain ? 'Главное фото' : 'Сделать главным'}
                      >
                        <img
                          src={pf.previewUrl}
                          alt={pf.file.name}
                          className={cn.pendingImageThumb}
                        />
                        {isMain && (
                          <div className={cn.pendingMainBadge}>
                            <StarFilled />
                          </div>
                        )}
                        <button
                          className={cn.pendingRemoveBtn}
                          onClick={(e) => {
                            e.stopPropagation()
                            removePendingFile(pf.id)
                          }}
                          title="Убрать"
                        >
                          <DeleteOutlined />
                        </button>
                        <span
                          className={[
                            cn.pendingImageName,
                            isMain ? cn.pendingImageNameMain : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {isMain ? 'Главное' : pf.file.name}
                        </span>
                      </div>
                    )
                  })}
                </div>
                <span className={cn.pendingImageHint}>
                  Нажмите на картинку, чтобы выбрать её главной
                </span>
              </div>
            )}

            {/* Existing images — only for single selection */}
            {singleCode !== null &&
              (productImagesLoading ? (
                <div className={cn.attachLoading}>
                  <Loader size={20} />
                </div>
              ) : productImages.length === 0 ? (
                <div className={cn.attachEmpty}>Изображения отсутствуют</div>
              ) : (
                <div className={cn.attachImageGrid}>
                  {productImages.map((img, idx) => (
                    <div
                      key={idx}
                      className={[
                        cn.attachImageCard,
                        img.isMain ? cn.attachImageCardMain : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <div className={cn.attachImageCardThumbWrap}>
                        <Image
                          productCode={singleCode!}
                          imageIndex={idx}
                          scope="all"
                          className={cn.attachImageCardThumb}
                          disableLoader
                        />
                      </div>
                      <div className={cn.attachImageCardFooter}>
                        <div className={cn.attachImageCardTitleGroup}>
                          <span
                            className={cn.attachImageCardName}
                            title={img.name || `Фото ${idx + 1}`}
                          >
                            {img.name || `Фото ${idx + 1}`}
                          </span>
                          {img.isMain && (
                            <span className={cn.attachImageMainLabel}>
                              Главное
                            </span>
                          )}
                        </div>
                        <Select
                          size="small"
                          value={
                            (img.type as AttachmentType | undefined) ?? 'IMAGE'
                          }
                          options={IMAGE_TYPE_OPTIONS}
                          disabled={!img.name}
                          onChange={(newType) =>
                            img.name &&
                            updateAttachFileType(
                              img.name,
                              img.extension,
                              newType
                            )
                          }
                          popupMatchSelectWidth={false}
                          className={cn.attachImageTypeSelect}
                        />
                        <span className={cn.attachImageCardExt}>
                          {img.extension.toUpperCase()}
                        </span>
                        {img.name && (
                          <button
                            className={cn.attachFileDeleteBtn}
                            onClick={() =>
                              confirmDelete(
                                img.name!,
                                (img.type as AttachmentType | undefined) ??
                                  'IMAGE',
                                img.name!
                              )
                            }
                            title="Удалить"
                          >
                            <DeleteOutlined />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
          </>
        )}

        {/* ══════════════ DOCUMENTS TAB ══════════════ */}
        {subTab === 'DOCUMENTS' && (
          <>
            <div className={cn.docCategoriesList}>
              {DOC_CATEGORIES.map((cat) => {
                const catPending = pendingFiles.filter(
                  (f) => f.type === cat.type
                )
                const catServer =
                  isSingle && !attachDocsLoading
                    ? attachDocs.filter((f) => f.type === cat.type)
                    : []
                const totalCount = catPending.length + catServer.length
                return (
                  <div key={cat.type} className={cn.docSection}>
                    {/* Label row */}
                    <div className={cn.docSectionLabel}>
                      <div className={cn.docSectionLabelLeft}>
                        {cat.label}
                        {totalCount > 0 && (
                          <span className={cn.docCategoryCount}>
                            {totalCount}
                          </span>
                        )}
                      </div>
                      {catPending.length > 0 && (
                        <div className={cn.docAddGroup}>
                          <span className={cn.docAddHint}>
                            Перетащите в область или
                          </span>
                          <label
                            className={[
                              cn.docAddBtn,
                              uploading ? cn.docAddBtnDisabled : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            title={`Добавить ещё — ${cat.label}`}
                          >
                            <PlusOutlined />
                            <input
                              type="file"
                              multiple
                              accept={cat.accept}
                              className={cn.hiddenFileInput}
                              disabled={uploading}
                              onChange={(e) => {
                                handleDocFiles(e.target.files, cat)
                                ;(e.target as HTMLInputElement).value = ''
                              }}
                            />
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Drop zone card for pending files */}
                    <div
                      className={[
                        cn.docCategory,
                        isDragging && dragOverCat !== cat.type
                          ? cn.docCategoryReady
                          : '',
                        dragOverCat === cat.type ? cn.docCategoryDragOver : '',
                        uploading ? cn.docCategoryDisabled : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onDragOver={(e) => {
                        e.preventDefault()
                        if (!uploading) setDragOverCat(cat.type)
                      }}
                      onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node))
                          setDragOverCat(null)
                      }}
                      onDrop={(e) => handleDocDrop(e, cat)}
                    >
                      {catPending.length === 0 ? (
                        <div className={cn.docCategoryEmpty}>
                          <div className={cn.docCategoryEmptyRow}>
                            <span>Перетащите файлы сюда или нажмите</span>
                            <label
                              className={[
                                cn.docAddBtn,
                                uploading ? cn.docAddBtnDisabled : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              title={`Добавить — ${cat.label}`}
                            >
                              <PlusOutlined />
                              <input
                                type="file"
                                multiple
                                accept={cat.accept}
                                className={cn.hiddenFileInput}
                                disabled={uploading}
                                onChange={(e) => {
                                  handleDocFiles(e.target.files, cat)
                                  ;(e.target as HTMLInputElement).value = ''
                                }}
                              />
                            </label>
                          </div>
                          <span className={cn.docCategoryFormats}>
                            {cat.accept.replace(/,/g, ' ')}
                          </span>
                        </div>
                      ) : (
                        <div className={cn.pendingDocList}>
                          {catPending.map((pf) => (
                            <div key={pf.id} className={cn.pendingDocItem}>
                              <div className={cn.attachDocIcon}>
                                {getFileIcon(
                                  pf.file.name.split('.').pop() ?? ''
                                )}
                              </div>
                              <div className={cn.attachDocInfo}>
                                <span className={cn.attachDocName}>
                                  {pf.file.name}
                                </span>
                                <span className={cn.attachDocExt}>
                                  {(pf.file.size / 1024).toFixed(0)} КБ
                                </span>
                              </div>
                              <button
                                className={cn.pendingRemoveBtnInline}
                                onClick={() => removePendingFile(pf.id)}
                                title="Убрать"
                              >
                                <DeleteOutlined />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Server docs for this category */}
                    {catServer.length > 0 && (
                      <div className={cn.serverDocList}>
                        {catServer.map((f) => (
                          <div
                            key={f.name ?? f.dvdan.slice(0, 8)}
                            className={cn.serverDocItem}
                          >
                            <div className={cn.attachDocIcon}>
                              {getFileIcon(f.extension)}
                            </div>
                            <div className={cn.attachDocInfo}>
                              <span className={cn.attachDocName}>{f.name}</span>
                              <span className={cn.attachDocExt}>
                                {f.extension.toUpperCase()}
                              </span>
                            </div>
                            <Select
                              size="small"
                              value={f.type}
                              options={DOC_CATEGORIES.map((c) => ({
                                value: c.type,
                                label: c.label,
                              }))}
                              onChange={(newType) =>
                                f.name &&
                                updateAttachFileType(
                                  f.name,
                                  f.extension,
                                  newType
                                )
                              }
                              popupMatchSelectWidth={false}
                              className={cn.attachTypeSelect}
                            />
                            <button
                              className={cn.attachFileActionBtn}
                              onClick={() => downloadAttachFile(f)}
                              title="Скачать"
                            >
                              <DownloadOutlined />
                            </button>
                            <button
                              className={cn.attachFileDeleteBtn}
                              onClick={() =>
                                f.name && confirmDelete(f.name, f.type, f.name)
                              }
                              title="Удалить"
                            >
                              <DeleteOutlined />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {isSingle && attachDocsLoading && (
              <div className={cn.attachLoading}>
                <Loader size={20} />
              </div>
            )}

            {isSingle && !attachDocsLoading && attachDocs.length === 0 && (
              <p className={cn.docsEmptyHint}>Документы отсутствуют</p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default AttachmentsTab
